import { createTheme, type ThemeOptions, type Theme } from '@mui/material/styles';

/**
 * Theme customization options
 */
export interface SapiensThemeOptions {
  /**
   * Primary color
   * @default '#1976d2'
   */
  primary?: string;

  /**
   * Secondary color
   * @default '#9c27b0'
   */
  secondary?: string;

  /**
   * Error color
   * @default '#d32f2f'
   */
  error?: string;

  /**
   * Warning color
   * @default '#ed6c02'
   */
  warning?: string;

  /**
   * Info color
   * @default '#0288d1'
   */
  info?: string;

  /**
   * Success color
   * @default '#2e7d32'
   */
  success?: string;

  /**
   * Theme mode
   * @default 'light'
   */
  mode?: 'light' | 'dark';

  /**
   * Font family
   * @default 'Roboto, "Helvetica Neue", Arial, sans-serif'
   */
  fontFamily?: string;

  /**
   * Border radius for components
   * @default 8
   */
  borderRadius?: number;

  /**
   * Additional theme overrides
   */
  overrides?: Partial<ThemeOptions>;
}

/**
 * Default Sapiens theme colors
 */
const defaultColors = {
  primary: '#1976d2',
  secondary: '#9c27b0',
  error: '#d32f2f',
  warning: '#ed6c02',
  info: '#0288d1',
  success: '#2e7d32',
};

/**
 * Create a customized Sapiens theme
 */
export function createSapiensTheme(options: SapiensThemeOptions = {}): Theme {
  const {
    primary = defaultColors.primary,
    secondary = defaultColors.secondary,
    error = defaultColors.error,
    warning = defaultColors.warning,
    info = defaultColors.info,
    success = defaultColors.success,
    mode = 'light',
    fontFamily = 'Roboto, "Helvetica Neue", Arial, sans-serif',
    borderRadius = 8,
    overrides = {},
  } = options;

  const baseTheme: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: primary,
      },
      secondary: {
        main: secondary,
      },
      error: {
        main: error,
      },
      warning: {
        main: warning,
      },
      info: {
        main: info,
      },
      success: {
        main: success,
      },
      background: {
        default: mode === 'light' ? '#f5f5f5' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
      },
    },
    typography: {
      fontFamily,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 500,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'light'
              ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
              : '0 1px 3px rgba(0,0,0,0.24), 0 1px 2px rgba(0,0,0,0.36)',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderBottom: mode === 'light'
              ? '1px solid rgba(0,0,0,0.12)'
              : '1px solid rgba(255,255,255,0.12)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius,
          },
        },
      },
    },
  };

  // Merge with overrides
  return createTheme({
    ...baseTheme,
    ...overrides,
    palette: {
      ...baseTheme.palette,
      ...overrides.palette,
    },
    components: {
      ...baseTheme.components,
      ...overrides.components,
    },
  });
}

/**
 * Default light theme
 */
export const lightTheme = createSapiensTheme({ mode: 'light' });

/**
 * Default dark theme
 */
export const darkTheme = createSapiensTheme({ mode: 'dark' });
